import { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useSession from '@/hooks/useSession';
import Form from '@/components/Form';
import PasswordInput from '@/components/PasswordInput';
import Button from '@/components/Button';
import HomeLayout from '@/layouts/HomeLayout';
import SEOHelmet from '@/components/SEOHelmet';
import { ErrorException } from '@/models/error';
import { gaIdentifyUser, gaPushEvent } from '@/utils/analytics';
import InputWrapper from '@/components/Form/InputWrapper';
import { toast } from 'react-toastify';
import { AuthService, OrgConfigResponse } from '@/services/auth.service';
import { getOrgIdentifier } from '@/utils/subdomain';
import Spinner from '@/assets/icons/spinner.svg?react';
import styles from './sign-in.module.scss';

type AuthMode = 'loading' | 'sso' | 'password';

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, setSession, isAuthenticated } = useSession();
  const from = location.state?.from?.pathname || '/';

  const userRef = useRef<HTMLInputElement>(null);
  const prevUserPwd = useRef({ user: '', pwd: '' });

  // Auth mode & org config
  const [authMode, setAuthMode] = useState<AuthMode>('loading');
  const [orgConfig, setOrgConfig] = useState<OrgConfigResponse | null>(null);
  const [ssoError, setSsoError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);

  // Password mode states
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState('');
  const [pwd, setPwd] = useState('');

  const navigateToApp = useCallback(() => {
    const search = location.search;
    navigate((from === '/' ? '/' : from) + search, { replace: true });
  }, [from, location.search, navigate]);

  // Check for org configuration on mount
  useEffect(() => {
    const checkOrgConfig = async () => {
      const orgIdentifier = getOrgIdentifier();

      // No org identifier = password mode (generic login)
      if (!orgIdentifier) {
        setAuthMode('password');
        return;
      }

      try {
        const config = await AuthService.getOrgConfig(orgIdentifier);
        setOrgConfig(config);

        // Store org config for use after SSO login
        localStorage.setItem(
          'workspace-org-config',
          JSON.stringify({
            organization_id: config.organization_id,
            name: config.name,
            logo_url: config.logo_url,
            auth_method: config.auth_method,
          })
        );

        if (config.auth_method === 'sso') {
          if (config.zitadel_auth_url) {
            setAuthMode('sso');
          } else {
            // SSO enforced but not configured
            setSsoError(
              "Single Sign-On is required for your organization, but it hasn't been fully configured yet. Please contact your administrator."
            );
            setAuthMode('sso');
          }
        } else {
          setAuthMode('password');
        }
      } catch (error) {
        console.error('Failed to fetch org config:', error);
        setSsoError(
          "We couldn't verify your organization's sign-in settings. Please contact your administrator or try the general sign-in page."
        );
        setAuthMode('sso');
      }
    };

    checkOrgConfig();
  }, []);

  // Focus input for password mode
  useEffect(() => {
    if (authMode === 'password' && userRef.current) {
      userRef.current.focus();
    }
  }, [authMode]);

  // Redirect if already authenticated
  useEffect(() => {
    if (session && session.email) {
      navigateToApp();
    }
  }, [from, navigate, navigateToApp, session]);

  // Dismiss toasts on input change (but not on initial mount to preserve SSO errors)
  useEffect(() => {
    const hasChanged =
      prevUserPwd.current.user !== user || prevUserPwd.current.pwd !== pwd;
    const wasEmpty =
      prevUserPwd.current.user === '' && prevUserPwd.current.pwd === '';

    // Only dismiss if values actually changed from non-empty state
    if (hasChanged && !wasEmpty) {
      toast.dismiss();
    }

    // Update previous values
    prevUserPwd.current = { user, pwd };
  }, [user, pwd]);

  // Handle SSO button click
  const handleSSOClick = () => {
    if (orgConfig?.zitadel_auth_url) {
      setLoading(true);

      // Extract and store state parameter for client-side CSRF validation
      // This provides defense-in-depth alongside backend validation
      try {
        const authUrl = new URL(orgConfig.zitadel_auth_url);
        const state = authUrl.searchParams.get('state');
        if (state) {
          sessionStorage.setItem('sso_oauth_state', state);
        }
      } catch {
        // If URL parsing fails, continue anyway - backend will still validate
        console.warn('Could not extract state from auth URL');
      }

      window.location.href = orgConfig.zitadel_auth_url;
    }
  };

  // Handle logo load error
  const handleLogoError = () => {
    setLogoError(true);
  };

  // Handle password form submit
  const handleSubmit = async () => {
    try {
      setLoading(true);

      const signedUser = await AuthService.login({
        email: user,
        password: pwd,
      });
      setSession(signedUser);

      if (isAuthenticated) {
        gaIdentifyUser(session.email);
        gaPushEvent('sign_in', user);
        navigateToApp();
      }
    } catch (err) {
      const error = err as ErrorException;
      toast(error.message, { type: 'error' });
      setLoading(false);
    }
  };

  // Render: Loading state
  if (authMode === 'loading') {
    return (
      <HomeLayout>
        <SEOHelmet
          title="Sign In"
          description="Sign in to GANNET"
          index={true}
        />
        <div className={styles['loading-container']}>
          <Spinner className={styles['spinner']} />
          <p>Loading...</p>
        </div>
      </HomeLayout>
    );
  }

  // Render: SSO mode
  if (authMode === 'sso') {
    // SSO error state
    if (ssoError) {
      return (
        <HomeLayout>
          <SEOHelmet
            title="Sign In Error"
            description="Unable to sign in"
            index={false}
          />
          <div className={styles['error-container']}>
            <h1>Unable to Sign In</h1>
            <p className={styles['error-message']}>{ssoError}</p>
            <p className={styles['error-help']}>
              Not a member of this organization?{' '}
              <a href="/sign-in">Go to general sign-in page</a>
            </p>
          </div>
        </HomeLayout>
      );
    }

    // SSO normal state
    return (
      <HomeLayout>
        <SEOHelmet
          title={`Sign in to ${orgConfig?.name || 'Your Organization'}`}
          description="Sign in with SSO"
          index={true}
        />

        <div className={styles['org-branding']}>
          {orgConfig?.logo_url && !logoError ? (
            <img
              src={orgConfig.logo_url}
              alt={orgConfig.name}
              onError={handleLogoError}
            />
          ) : (
            <h2 className={styles['org-name-styled']}>{orgConfig?.name}</h2>
          )}
        </div>

        <h1>Sign in to {orgConfig?.name}</h1>
        <p className={styles['sso-description']}>
          {orgConfig?.idp_name
            ? `You'll be redirected to ${orgConfig.idp_name} to sign in.`
            : "You'll be redirected to your organization's identity provider."}
        </p>

        <Button
          variation="alt"
          onClick={handleSSOClick}
          loading={loading}
          loadingText="Redirecting"
          className={styles['sso-button']}
        >
          Continue with SSO
        </Button>
      </HomeLayout>
    );
  }

  // Render: Password mode
  return (
    <HomeLayout>
      <SEOHelmet title="Sign In" description="Sign in to GANNET" index={true} />
      <h1>Sign In</h1>
      <Form onSubmit={() => handleSubmit()}>
        <InputWrapper hideRequiredStyle={true}>
          <input
            type="text"
            id="username"
            name="username"
            ref={userRef}
            autoComplete="off"
            onChange={(e) => setUser(e.target.value)}
            value={user}
            required
          />
          <label htmlFor="username">Email:</label>
        </InputWrapper>
        <PasswordInput
          hideRequiredStyle={true}
          password={pwd}
          setPassword={setPwd}
        />
        <Button variation="alt" loading={loading}>
          Sign In
        </Button>
      </Form>
    </HomeLayout>
  );
};

export default SignIn;
